// This value must match CACHE_NAME in public/sw.js
const CACHE_NAME: string = 'safety-genie-v3';
// If you know of additional routes that it is safe to refresh on, then please add them to the array
// so that we can have additional coverage to apply updates to
const ACCEPTABLE_REFRESH_PATHS = ['/home']

let updateAvailable: boolean = false;

/**
 * Returns whether a website update has been detected and is pending for the user to route to a path where
 * applying a refresh is acceptable.
 */
export const isUpdateAvailable = (): boolean => updateAvailable;

/**
 * Periodically checks if a newer version of the website is available by comparing
 * the cached index.html against the latest version from the network.
 *
 * If an update is detected and the user is on the home page, the page is reloaded
 * immediately to apply the update. We do this so if the user is mid-workflow, the update is deferred
 * until the next check when they are on the home page. That way their work doesn't get interrupted.
 *
 * Called on an interval from App.tsx. The service worker's stale-while-revalidate
 * strategy handles freshness on initial load, so this covers long-lived sessions where the user never closes out of
 * the PWA, closes their browser, or even refreshes the site.
 */
export function checkForWebsiteUpdates() {
    // Only reload the website if they are on the home page so their workflow is not interrupted
    if (updateAvailable && ACCEPTABLE_REFRESH_PATHS.includes(window.location.pathname)) {
        // TODO: Add a UI element when the page is refreshed due to an update being applied https://taskei.amazon.dev/tasks/P415250778
        window.location.reload();
        return;
    }

    caches.open(CACHE_NAME).then((cache: Cache) => {
        cache.match("/").then((cacheResponse: Response | undefined) => {
            fetch("/index.html", { cache: 'no-store' }).then((networkResponse: Response) => {
                if (!networkResponse.ok) {
                    console.error('checkForWebsiteUpdates: non-ok response', networkResponse.status);
                    return;
                }
                const networkClone = networkResponse.clone();
                Promise.all([
                    networkResponse.text(),
                    cacheResponse?.text()
                ]).then(([networkText, cacheText]) => {
                    if (networkText !== cacheText) {
                        // Update the cache before reloading
                        cache.put("/", networkClone).then(() => {
                            if (ACCEPTABLE_REFRESH_PATHS.includes(window.location.pathname)) {
                                // TODO: Add a UI element when the page is refreshed due to an update being applied https://taskei.amazon.dev/tasks/P415250778
                                window.location.reload();
                            } else {
                                updateAvailable = true;
                            }
                        });
                    }
                });
            }).catch((error) => {
                console.error('checkForWebsiteUpdates: failed to fetch latest index.html', error);
            });
        }).catch((error) => {
            console.error('checkForWebsiteUpdates: failed to read cached index.html', error);
        });
    }).catch((error) => {
        console.error('checkForWebsiteUpdates: failed to open cache', error);
    });
}
